import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, MapPin, Users } from 'lucide-react';
import { funnelService } from '../../../services/funnelService';

const CalendarView = ({ userId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCalendarEvents();
  }, [currentDate]);

  const loadCalendarEvents = async () => {
    setLoading(true);
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    try {
      const { data, error: eventError } = await funnelService?.getCalendarEvents(
        userId,
        startDate?.toISOString(),
        endDate?.toISOString()
      );
      
      if (eventError) {
        setError(eventError);
      } else {
        setEvents(data || []);
      }
    } catch (err) {
      setError('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    
    return events?.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate?.toDateString() === date?.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(newDate?.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateClick = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const formatTime = (dateString) => {
    return new Date(dateString)?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      meeting: 'bg-blue-100 text-blue-800 border-blue-200',
      call: 'bg-green-100 text-green-800 border-green-200',
      demo: 'bg-purple-100 text-purple-800 border-purple-200',
      follow_up: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      proposal: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors?.[eventType] || colors?.meeting;
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">Sales Calendar</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h4 className="text-lg font-medium text-gray-900 min-w-40 text-center">
              {monthNames?.[currentDate?.getMonth()]} {currentDate?.getFullYear()}
            </h4>
            <button
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <button
          onClick={handleCreateEvent}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Event
        </button>
      </div>
      <div className="flex gap-6">
        {/* Calendar Grid */}
        <div className="flex-1">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-0 bg-gray-50">
              {dayNames?.map((day) => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-0">
              {days?.map((date, index) => {
                const dateEvents = getEventsForDate(date);
                const isToday = date && date?.toDateString() === new Date()?.toDateString();
                const isSelected = date && date?.toDateString() === selectedDate?.toDateString();

                return (
                  <div
                    key={index}
                    onClick={() => handleDateClick(date)}
                    className={`
                      min-h-24 p-1 border-r border-b border-gray-200 cursor-pointer hover:bg-gray-50
                      ${!date ? 'bg-gray-100 cursor-default' : ''}
                      ${isSelected ? 'bg-blue-50 border-blue-200' : ''}
                    `}
                  >
                    {date && (
                      <>
                        <div className="flex justify-between items-start">
                          <span
                            className={`
                              text-sm font-medium
                              ${isToday ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-900'}
                            `}
                          >
                            {date?.getDate()}
                          </span>
                        </div>
                        
                        {/* Events */}
                        <div className="mt-1 space-y-1">
                          {dateEvents?.slice(0, 2)?.map((event) => (
                            <div
                              key={event?.id}
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleEventClick(event);
                              }}
                              className={`
                                px-2 py-1 text-xs rounded border truncate cursor-pointer hover:shadow-sm
                                ${getEventTypeColor(event?.event_type)}
                              `}
                              title={event?.title}
                            >
                              {formatTime(event?.start_time)} - {event?.title}
                            </div>
                          ))}
                          {dateEvents?.length > 2 && (
                            <div className="text-xs text-gray-500 px-1">
                              +{dateEvents?.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Side Panel - Selected Date Events */}
        <div className="w-80">
          <div className="bg-white shadow rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {selectedDate?.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h4>

            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              {getEventsForDate(selectedDate)?.map((event) => (
                <div
                  key={event?.id}
                  onClick={() => handleEventClick(event)}
                  className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 text-sm">{event?.title}</h5>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center text-xs text-gray-600">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatTime(event?.start_time)} - {formatTime(event?.end_time)}
                        </div>
                        {event?.location && (
                          <div className="flex items-center text-xs text-gray-600">
                            <MapPin className="h-3 w-3 mr-1" />
                            {event?.location}
                          </div>
                        )}
                        {event?.funnel_leads?.communication_contacts?.name && (
                          <div className="flex items-center text-xs text-gray-600">
                            <Users className="h-3 w-3 mr-1" />
                            {event?.funnel_leads?.communication_contacts?.name}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded border ${getEventTypeColor(event?.event_type)}`}>
                      {event?.event_type?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}

              {getEventsForDate(selectedDate)?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No events scheduled</p>
                  <button
                    onClick={handleCreateEvent}
                    className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Create event for this day
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-md shadow-lg">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedEvent ? 'Event Details' : 'Create New Event'}
                </h3>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              {selectedEvent ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Title</label>
                    <p className="text-sm text-gray-900">{selectedEvent?.title}</p>
                  </div>
                  
                  {selectedEvent?.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Description</label>
                      <p className="text-sm text-gray-900">{selectedEvent?.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Time</label>
                    <p className="text-sm text-gray-900">
                      {formatTime(selectedEvent?.start_time)} - {formatTime(selectedEvent?.end_time)}
                    </p>
                  </div>
                  
                  {selectedEvent?.location && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Location</label>
                      <p className="text-sm text-gray-900">{selectedEvent?.location}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Type</label>
                    <p className="text-sm text-gray-900 capitalize">
                      {selectedEvent?.event_type?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Event creation form would go here</p>
                  <p className="text-xs text-gray-400 mt-1">
                    This would integrate with your calendar system
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;